import React, { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Box,
  Divider,
  Stack,
} from "@mui/material";
import { Lock, LockOpen } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useSearchEvidenceRequests,
  useUpdateEvidenceRequestBlocks,
} from "../../../hooks/evidencerequests";
import { EvidenceRequest } from "../../../types/evidencerequests";
import { debounce } from "lodash";
import { getUserInfo } from "../../../helpers/user";

interface BlockingRelationshipsProps {
  evidenceRequest: EvidenceRequest;
}

const BlockingRelationships: React.FC<BlockingRelationshipsProps> = ({
  evidenceRequest,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { mutate: updateEvidenceRequest } = useUpdateEvidenceRequestBlocks();

  useEffect(() => {
    const user = getUserInfo();
    setIsAdmin(user.role === "admin" || user.role === "auditor");
  }, []);

  const { data: searchResults = [], refetch: refetchEvidenceRequests } =
    useSearchEvidenceRequests({
      q: searchQuery,
      organization: evidenceRequest.organization?.id,
    });

  const filteredSearchResults = useMemo(() => {
    if (!searchResults[0]?.evidencerequests) return [];

    const existingIds = new Set([
      ...(evidenceRequest.blocked_by || []).map((req) => req.id),
      ...(evidenceRequest.blocks || []).map((req) => req.id),
      evidenceRequest.id,
    ]);

    return searchResults[0].evidencerequests.filter(
      (result: EvidenceRequest) =>
        !existingIds.has(result.id) && result.status !== "Accepted"
    );
  }, [searchResults, evidenceRequest]);

  const handleSearchChange = debounce((value: string) => {
    setSearchQuery(value);
    refetchEvidenceRequests();
  }, 300);

  const handleAddBlockingRequest = (request: EvidenceRequest | null) => {
    if (!request) return;
    updateEvidenceRequest({
      ...evidenceRequest,
      blocked_by: [...(evidenceRequest.blocked_by || []), request],
    });
  };

  const handleRemoveBlockingRequest = (requestToRemove: EvidenceRequest) => {
    updateEvidenceRequest({
      ...evidenceRequest,
      blocked_by: (evidenceRequest.blocked_by || []).filter(
        (request) => request.id !== requestToRemove.id
      ),
    });
  };

  const handleAddBlockedRequest = (request: EvidenceRequest | null) => {
    if (!request) return;
    updateEvidenceRequest({
      ...evidenceRequest,
      blocks: [...(evidenceRequest.blocks || []), request],
    });
  };

  const handleRemoveBlockedRequest = (requestToRemove: EvidenceRequest) => {
    updateEvidenceRequest({
      ...evidenceRequest,
      blocks: (evidenceRequest.blocks || []).filter(
        (request) => request.id !== requestToRemove.id
      ),
    });
  };

  const renderChip = (
    request: EvidenceRequest,
    handleDelete: (req: EvidenceRequest) => void
  ) => (
    <Chip
      key={request.id}
      label={request.title || request.id}
      {...(isAdmin ? { onDelete: () => handleDelete(request) } : {})}
      onClick={(event) => {
        if (!(event.target as HTMLElement).tagName.match(/^(svg|path)$/i)) {
          navigate(`/evidence-request/${request.id}`);
        }
      }}
      clickable
      sx={{
        m: 0.5,
        "&:hover": {
          backgroundColor: "primary.light",
        },
      }}
      color={request.status === "Accepted" ? "success" : "default"}
    />
  );

  const renderSection = (
    title: string,
    icon: JSX.Element,
    description: string,
    items: EvidenceRequest[] | undefined,
    handleDelete: (req: EvidenceRequest) => void,
    handleAdd: (req: EvidenceRequest | null) => void,
    placeholder: string
  ) => (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="subtitle1"
        sx={{ mb: 1, display: "flex", alignItems: "center" }}
      >
        {icon} {title}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        {description}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
        {items?.map((request) => renderChip(request, handleDelete))}
      </Stack>

      {isAdmin && (
        <Autocomplete
          options={filteredSearchResults}
          loading={searchResults.length === 0}
          noOptionsText={"Start typing to search for evidence requests"}
          getOptionLabel={(option: EvidenceRequest) =>
            option.title || option.id
          }
          onChange={(_, value) => {
            handleAdd(value);
            setSearchQuery("");
          }}
          value={null}
          clearOnBlur={true}
          clearOnEscape={true}
          onInputChange={(_, value) => {
            handleSearchChange(value);
          }}
          filterOptions={(x) => x}
          renderInput={(params) => (
            <TextField {...params} label={placeholder} variant="outlined" />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1">
                  {option.title || option.id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Status: {option.status}
                </Typography>
              </Box>
            </li>
          )}
        />
      )}
    </Box>
  );

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Blocking Relationships
        </Typography>

        {renderSection(
          "Blocked By",
          <Lock sx={{ mr: 1 }} />,
          "This request cannot be submitted for review until the following requests are accepted",
          evidenceRequest.blocked_by,
          handleRemoveBlockingRequest,
          handleAddBlockingRequest,
          "Add blocking request"
        )}

        <Divider sx={{ my: 3 }} />

        {renderSection(
          "Blocks",
          <LockOpen sx={{ mr: 1 }} />,
          "This request blocks the following requests from being submitted for review",
          evidenceRequest.blocks,
          handleRemoveBlockedRequest,
          handleAddBlockedRequest,
          "Add request to block"
        )}
      </CardContent>
    </Card>
  );
};

export default BlockingRelationships;
